import { useState, useEffect } from 'react';
import './common.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import config from '../config';
import { sanitizeTextInput } from '../sanitize';

function SpellingAssistant({ onNavigate }) {

  // Form state
  const [formData, setFormData] = useState({
    grade: 8,
    difficultyLevel: 'medium',
    maxWordLength: 6,
    context: '',
    numberOfWords: 5,
    language: 'English',
    hints: true
  });

  // Quiz state
  const [words, setWords] = useState([]);
  const [userAnswers, setUserAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [answeredWords, setAnsweredWords] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [errorPopup, setErrorPopup] = useState({ show: false, message: '', correctWord: '', userTyped: '', index: -1 });
  const [showInstructions, setShowInstructions] = useState(false);
  const [incorrectAnswers, setIncorrectAnswers] = useState([]);

  // Load voices for speech synthesis
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Load voices
      window.speechSynthesis.getVoices();
      // Some browsers need this event
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const startQuiz = async () => {
    if (!formData.context.trim()) {
      setError('Please enter a context for the words');
      return;
    }

    const ageValue = formData.grade;
    if (ageValue === '' || isNaN(Number(ageValue)) || !Number.isInteger(Number(ageValue))) {
      setError('Please enter a valid age (numbers only)');
      return;
    }
    const age = parseInt(ageValue);
    if (age < 5 || age > 18) {
      setError('Please enter an age between 5 and 18');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(config.endpoints.spellingQuiz, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          age: age,
          difficultyLevel: formData.difficultyLevel,
          context: sanitizeTextInput(formData.context),
          numberOfWords: parseInt(formData.numberOfWords),
          language: formData.language,
          hints: formData.hints
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate words: ${errorText}`);
      }

      const data = await response.json();
      setWords(data);
      setShowQuiz(true);
      setScore(0);
      setAnsweredWords(new Array(data.length).fill(false));
      setUserAnswers(new Array(data.length).fill(''));
      setIncorrectAnswers(new Array(data.length).fill(false));
      
      // Start timer
      const now = Date.now();
      setElapsedTime(0);
      const interval = setInterval(() => {
        setElapsedTime(Date.now() - now);
      }, 1000);
      setTimerInterval(interval);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const speakWord = (index) => {
    if ('speechSynthesis' in window && words[index]) {
      const currentWord = words[index];
      const wordToSpeak = currentWord.word;
      
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(wordToSpeak);
      
      // Detect if word contains Tamil characters
      const isTamil = /[\u0B80-\u0BFF]/.test(wordToSpeak);
      
      const voices = window.speechSynthesis.getVoices();

      if (isTamil || currentWord.language === 'Tamil') {
        utterance.lang = 'ta-IN';
        // Try to find a female Tamil voice first, then any Tamil voice
        const tamilVoice =
          voices.find(v => (v.lang.startsWith('ta') || v.lang === 'ta-IN') && /female|woman/i.test(v.name)) ||
          voices.find(v => v.lang.startsWith('ta') || v.lang === 'ta-IN');
        if (tamilVoice) {
          utterance.voice = tamilVoice;
        }
      } else {
        utterance.lang = 'en-US';
        // Prefer a female English voice (e.g. Zira on Windows, Samantha on macOS, Google UK English Female)
        const femaleVoice =
          voices.find(v => v.lang.startsWith('en') && /zira|samantha|victoria|karen|moira|fiona|veena|female|woman/i.test(v.name)) ||
          voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes('female'));
        if (femaleVoice) {
          utterance.voice = femaleVoice;
        }
      }
      
      utterance.rate = 0.8;
      utterance.pitch = 4.0;
      utterance.volume = 1.0;
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const checkAnswer = (index) => {
    const userWord = userAnswers[index]?.trim().toLowerCase();
    const correctWord = words[index].word.toLowerCase();

    if (!userWord) {
      setErrorPopup({ show: true, message: 'Please type a word first! 📝', index });
      setTimeout(() => setErrorPopup({ show: false, message: '', index: -1 }), 2000);
      return;
    }

    if (userWord === correctWord) {
      if (!answeredWords[index]) {
        setScore(score + 1);
        const newAnswered = [...answeredWords];
        newAnswered[index] = true;
        setAnsweredWords(newAnswered);
      }
      setErrorPopup({ show: true, message: '🎉 Perfect! You got it right!', index });
      setTimeout(() => setErrorPopup({ show: false, message: '', index: -1 }), 2000);
    } else {
      setErrorPopup({ 
        show: true, 
        message: 'wrong',
        correctWord: words[index].word,
        userTyped: userAnswers[index].trim(),
        index 
      });
      // stays open until user closes it
    }
  };

  const handleUserAnswerChange = (index, value) => {
    const newAnswers = [...userAnswers];
    newAnswers[index] = value;
    setUserAnswers(newAnswers);
    if (incorrectAnswers[index]) {
      const newIncorrect = [...incorrectAnswers];
      newIncorrect[index] = false;
      setIncorrectAnswers(newIncorrect);
    }
  };

  const validateAllAnswers = () => {
    const incorrect = words.map((word, index) => {
      const userAnswer = userAnswers[index]?.trim().toLowerCase();
      return !userAnswer || userAnswer !== word.word.toLowerCase();
    });
    setIncorrectAnswers(incorrect);
  };

  const calculateScorePercentage = () => {
    const totalCorrect = words.reduce((count, question, index) => {
      const userAnswer = userAnswers[index];
      const isCorrect = userAnswer && userAnswer.toLowerCase() === question.word.toLowerCase();
      return count + (isCorrect ? 1 : 0);
    }, 0);

    return ((totalCorrect / words.length) * 100).toFixed(2);
  };

  const stopQuiz = () => {
    // Stop timer
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    // Capture duration and convert to seconds
    const durationInSeconds = Math.floor(elapsedTime / 1000);
    const score = calculateScorePercentage();

    setShowQuiz(false);
    setWords([]);
    setUserAnswers([]);
    setAnsweredWords([]);

    alert(`Quiz completed! Your score: ${score}%. Duration: ${durationInSeconds} seconds.`);
  };
  
  // Format elapsed time for display
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (showQuiz && words.length > 0) {
    return (
      <div className="spelling-container">
        <Header onNavigate={onNavigate} showHomeButton={true} />
        <div className="quiz-header">
          <h1 className="quiz-title">🎯 Spelling Practice</h1>
          <div className="score-timer-container">
            <div className="score-display">
              Score: {score} / {words.length}
            </div>
            <div className="timer-display">
              ⏱️ Time: {formatTime(elapsedTime)}
            </div>
          </div>
        </div>

        <div className="quiz-card">
          <div className="instructions-box">
            <button
              className="instructions-toggle"
              onClick={() => setShowInstructions(prev => !prev)}
            >
              {showInstructions ? '🙈 Hide Instructions' : '📝 Show Instructions'}
            </button>
            {showInstructions && (
              <ol className="instructions-list">
                <li>Click the 🔊 speaker button to hear each word</li>
                <li>Type the word you heard in the text box</li>
                <li>Hover over the 💡 icon to see a helpful hint</li>
                <li>Click "Check Answer" to check your answer - a popup will show if you're right or wrong!</li>
                <li>When done, click "✔️ Validate All" to check all your answers at once!</li>
              </ol>
            )}
          </div>

          <div className="word-counter">
            Total Words: {words.length}
          </div>

          {words.map((currentWord, index) => (
            <div key={index} className="word-row" style={{ position: 'relative' }}>
              {errorPopup.show && errorPopup.index === index && (
                <div className={`error-popup ${errorPopup.message === 'wrong' ? 'error-popup-danger' : 'success-popup'}`}>
                  {errorPopup.message === 'wrong' ? (
                    <>
                      <span className="popup-wrong">{errorPopup.userTyped}</span>
                      <span className="popup-arrow">→</span>
                      <span className="popup-correct">{errorPopup.correctWord}</span>
                      <button
                        className="popup-close-btn"
                        onClick={() => setErrorPopup({ show: false, message: '', correctWord: '', userTyped: '', index: -1 })}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    errorPopup.message
                  )}
                </div>
              )}
              <span className="word-number">{index + 1}.</span>
              
              <button 
                className="speaker-btn-inline"
                onClick={() => speakWord(index)}
                title="Click to hear the word"
              >
                🔊
              </button>

              <input
                type="text"
                className={`answer-input-inline${incorrectAnswers[index] ? ' answer-input-incorrect' : ''}`}
                value={userAnswers[index]}
                onChange={(e) => handleUserAnswerChange(index, e.target.value)}
                placeholder="Type the word here..."
                autoFocus={index === 0}
              />

          
              <button className="check-btn-inline" onClick={() => checkAnswer(index)} title="Check your answer">
                Submit ✅
              </button>
              {formData.hints && currentWord.phoneticHint && (
                <div className="hint-icon-container" title={currentWord.phoneticHint}>
                  <span className="hint-icon">💡</span>
                  <div className="hint-tooltip">
                    {currentWord.phoneticHint}
                  </div>
                </div>
              )}
    
            </div>
          ))}

          <div className="validate-all-container">
            <button className="validate-all-btn" onClick={validateAllAnswers}>
              ✔️ Validate All Answers
            </button>
            <button className="stop-btn" onClick={stopQuiz}>
              ⏹ Stop Quiz
            </button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="spelling-container">
      <Header onNavigate={onNavigate} showHomeButton={true} />
      <div className="setup-header">
        <h1 className="setup-title">✏️ Spelling Assistant Setup</h1>
        <p className="setup-description">
          Let's create a fun spelling quiz just for you!
        </p>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <div className="setup-form">
        <div className="form-row">
          <div className="form-group">
            <label>Age</label>
            <input
              type="text"
              name="grade"
              value={formData.grade}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter age (e.g. 8)"
            />
          </div>

          <div className="form-group">
            <label>Difficulty Level</label>
            <select
              name="difficultyLevel"
              value={formData.difficultyLevel}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Number of Words</label>
            <input
              type="number"
              name="numberOfWords"
              value={formData.numberOfWords}
              onChange={handleInputChange}
              className="form-input"
              min="1"
              max="20"
            />
          </div>

          <div className="form-group">
            <label>Context / Topic</label>
            <input
              type="text"
              name="context"
              value={formData.context}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., animals, sports, food, etc."
            />
          </div>
        </div>

        <div className="form-group">
          <label>Language</label>
          <select
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            className="form-select"
          >
            <option value="English">English</option>
            
          </select>
        </div>

        <div className="form-row">
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hints"
                checked={formData.hints}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span>Show Phonetic Hints</span>
            </label>
          </div>
        </div>

        <button
          className="start-btn"
          onClick={startQuiz}
          disabled={loading}
        >
          {loading ? '🔄 Generating Words...' : '🚀 Start Quiz'}
        </button>
      </div>
      <Footer />
    </div>
  );
}

export default SpellingAssistant;
